/**
 * The ripples the backdrop draws: rings that spread from where a seal
 * landed (or close in, for the opening's condensing sea), at most sixteen at
 * once, each for 2.6 s. The conductor adds them; this keeps them and packs
 * them for the frame, eight floats each. Pure.
 */

import type { Rgb } from './palette';

type Pt = { x: number; y: number };

export interface Ripple {
  readonly space: 'stage' | 'screen';
  readonly x: number;
  readonly y: number;
  readonly t0: number;
  readonly hue: number;
  readonly rgb: Rgb;
  readonly width: number;
  readonly strength: number;
  /** A ring closing in (the opening's condensing sea), not spreading out. */
  readonly converge?: boolean;
}

export const RIPPLE_LIFE = 2600;
export const MAX_RIPPLES = 16;

export class RippleSet {
  private readonly list: Ripple[] = [];
  /** What the frame reads: x, y, age (s), hue, r, g, b, and the signed width. */
  readonly out = new Float32Array(MAX_RIPPLES * 8);

  get size(): number {
    return this.list.length;
  }

  add(r: Omit<Ripple, 't0'>, at: number): void {
    this.list.push({ ...r, t0: at });
    while (this.list.length > MAX_RIPPLES) this.list.shift();
  }

  clear(): void {
    this.list.length = 0;
  }

  /** Drop the spent ones and pack the rest; `toScreen` places a stage-space ring. Returns how many. */
  pack(clock: number, toScreen: (x: number, y: number) => Pt): number {
    for (let i = this.list.length - 1; i >= 0; i--) {
      if (clock - this.list[i]!.t0 >= RIPPLE_LIFE) this.list.splice(i, 1);
    }
    let n = 0;
    for (const r of this.list) {
      const p = r.space === 'stage' ? toScreen(r.x, r.y) : r;
      const o = n * 8;
      this.out[o] = p.x; this.out[o + 1] = p.y; this.out[o + 2] = (clock - r.t0) / 1000; this.out[o + 3] = r.hue;
      this.out[o + 4] = r.rgb[0]; this.out[o + 5] = r.rgb[1]; this.out[o + 6] = r.rgb[2];
      this.out[o + 7] = (r.converge ? -1 : 1) * r.width * r.strength;
      n++;
    }
    return n;
  }
}
